import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Tooltip,
  FormHelperText,
  Switch,
  FormControlLabel,
  Grid,
  Avatar,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  History as HistoryIcon,
  VpnKey as PasswordIcon
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/user';
import { useAuth } from '../../context/SimpleAuthContext';
import { useCompany } from '../../contexts/CompanyContext';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Mock user data with more fields
const mockUsers = [
  { 
    id: 1, 
    name: 'Admin User', 
    email: 'admin@example.com', 
    role: 'admin', 
    status: 'Active',
    phone: '+1 234 567 8900',
    department: 'IT',
    lastLogin: '2025-05-28 09:00 AM',
    createdAt: '2024-01-15',
    companies: ['ModularCompliance Inc.', 'Tech Corp']
  },
  { 
    id: 2, 
    name: 'John Smith', 
    email: 'john.smith@example.com', 
    role: 'compliance_officer', 
    status: 'Active',
    phone: '+1 234 567 8901',
    department: 'Compliance',
    lastLogin: '2025-05-27 03:30 PM',
    createdAt: '2024-03-20',
    companies: ['ModularCompliance Inc.']
  },
  { 
    id: 3, 
    name: 'Jane Doe', 
    email: 'jane.doe@example.com', 
    role: 'risk_specialist', 
    status: 'Active',
    phone: '+1 234 567 8902',
    department: 'Risk Management',
    lastLogin: '2025-05-26 11:45 AM',
    createdAt: '2024-05-10',
    companies: ['ModularCompliance Inc.', 'Finance Ltd']
  },
  { 
    id: 4, 
    name: 'Bob Johnson', 
    email: 'bob.johnson@example.com', 
    role: 'task_implementer', 
    status: 'Inactive',
    phone: '+1 234 567 8903',
    department: 'Operations',
    lastLogin: '2025-04-15 02:00 PM',
    createdAt: '2024-02-28',
    companies: ['Tech Corp']
  },
  { 
    id: 5, 
    name: 'Sarah Williams', 
    email: 'sarah.williams@example.com', 
    role: 'department_manager', 
    status: 'Active',
    phone: '+1 234 567 8904',
    department: 'Finance',
    lastLogin: '2025-05-28 08:15 AM',
    createdAt: '2024-06-01',
    companies: ['ModularCompliance Inc.']
  }
];

// Available roles for dropdown
const availableRoles = [
  { value: 'admin', label: 'Administrator' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'risk_specialist', label: 'Risk & Control Specialist' },
  { value: 'implementation_officer', label: 'Compliance Implementation Officer' },
  { value: 'department_manager', label: 'Department Manager' },
  { value: 'task_implementer', label: 'Task Implementer' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'executive', label: 'Executive' },
  { value: 'document_manager', label: 'Document Manager' },
  { value: 'external_consultant', label: 'External Consultant' }
];

// Mock companies
const mockCompanies = [
  'ModularCompliance Inc.',
  'Tech Corp',
  'Finance Ltd',
  'Healthcare Systems',
  'Manufacturing Co.'
];

export default function MaterialUsersPage() {
  const { hasPermission } = usePermissions();
  const { user: currentUser } = useAuth();
  const { currentCompany } = useCompany();
  
  // Permission checks using the Permission enum imported as a value
  const canCreateUsers = hasPermission(Permission.USER_CREATE);
  const canEditUsers = hasPermission(Permission.USER_EDIT);
  const canDeleteUsers = hasPermission(Permission.USER_DELETE);

  // State
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [deletingUser, setDeletingUser] = useState<any>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: '',
    status: 'Active',
    phone: '',
    department: '',
    companies: [] as string[],
    password: '',
    confirmPassword: ''
  });

  const [formErrors, setFormErrors] = useState<any>({});

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus = !filterStatus || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Handle user creation/edit dialog
  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      name: '',
      email: '',
      role: '',
      status: 'Active',
      phone: '',
      department: '',
      companies: [currentCompany?.name || ''],
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setTabValue(0);
    setUserDialogOpen(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      phone: user.phone || '',
      department: user.department || '',
      companies: user.companies || [],
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setTabValue(0);
    setUserDialogOpen(true);
  };

  const handleDeleteUser = (user: any) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const handleResetPassword = (user: any) => {
    setResetPasswordUser(user);
    setResetPasswordDialogOpen(true);
  };

  // Validate form
  const validateForm = () => {
    const errors: any = {};
    
    if (!userForm.name.trim()) errors.name = 'Name is required';
    if (!userForm.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(userForm.email)) errors.email = 'Invalid email format';
    
    if (!userForm.role) errors.role = 'Role is required';
    
    if (!editingUser) {
      if (!userForm.password) errors.password = 'Password is required';
      else if (userForm.password.length < 8) errors.password = 'Password must be at least 8 characters';
      
      if (userForm.password !== userForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    if (userForm.companies.length === 0) errors.companies = 'At least one company is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save user
  const handleSaveUser = () => {
    if (!validateForm()) return;

    if (editingUser) {
      // Update existing user
      setUsers(users.map(u => 
        u.id === editingUser.id 
          ? { 
              ...u, 
              name: userForm.name,
              email: userForm.email,
              role: userForm.role,
              status: userForm.status,
              phone: userForm.phone,
              department: userForm.department,
              companies: userForm.companies
            }
          : u
      ));
      setSuccessMessage('User updated successfully');
    } else {
      // Create new user
      const newUser = {
        id: Math.max(...users.map(u => u.id)) + 1,
        name: userForm.name,
        email: userForm.email,
        role: userForm.role,
        status: userForm.status,
        phone: userForm.phone,
        department: userForm.department,
        companies: userForm.companies,
        lastLogin: 'Never',
        createdAt: new Date().toISOString().split('T')[0]
      };
      setUsers([...users, newUser]);
      setSuccessMessage('User created successfully');
    }
    
    setShowSuccessAlert(true);
    setUserDialogOpen(false);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    setUsers(users.filter(u => u.id !== deletingUser.id));
    setDeleteDialogOpen(false);
    setDeletingUser(null);
    setSuccessMessage('User deleted successfully');
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  // Handle password reset
  const handleConfirmResetPassword = () => {
    // In a real app, this would send a password reset email
    setResetPasswordDialogOpen(false);
    setSuccessMessage(`Password reset email sent to ${resetPasswordUser.email}`);
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  // Export users
  const handleExportUsers = () => {
    // In a real app, this would export to CSV/Excel
    console.log('Exporting users...');
    setSuccessMessage('Users exported successfully');
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  return (
    <Box>
      {/* Success Alert */}
      {showSuccessAlert && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowSuccessAlert(false)}>
          {successMessage}
        </Alert>
      )}

      {/* Page header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          User Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportUsers}
          >
            Export
          </Button>
          {canCreateUsers && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
            >
              Add User
            </Button>
          )}
        </Stack>
      </Box>

      {/* Search and filters */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search users by name or email..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Role</InputLabel>
            <Select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              label="Filter by Role"
            >
              <MenuItem value="">All Roles</MenuItem>
              {availableRoles.map(role => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Users table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Companies</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No users found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">
                        {user.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {availableRoles.find(r => r.value === user.role)?.label || user.role}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.department}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {user.companies.slice(0, 2).map((company, idx) => (
                        <Chip key={idx} label={company} size="small" variant="outlined" />
                      ))}
                      {user.companies.length > 2 && (
                        <Chip 
                          label={`+${user.companies.length - 2}`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={user.status === 'Active' ? <ActiveIcon /> : <InactiveIcon />}
                      label={user.status}
                      color={user.status === 'Active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {user.lastLogin}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Reset Password">
                        <IconButton 
                          size="small" 
                          onClick={() => handleResetPassword(user)}
                        >
                          <PasswordIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEditUsers && (
                        <Tooltip title="Edit User">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditUser(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteUsers && user.id !== currentUser?.id && (
                        <Tooltip title="Delete User">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteUser(user)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User stats */}
      <Grid container spacing={2} mt={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Users
            </Typography>
            <Typography variant="h4">
              {users.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Active Users
            </Typography>
            <Typography variant="h4" color="success.main">
              {users.filter(u => u.status === 'Active').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Inactive Users
            </Typography>
            <Typography variant="h4" color="text.secondary">
              {users.filter(u => u.status === 'Inactive').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Roles in Use
            </Typography>
            <Typography variant="h4">
              {new Set(users.map(u => u.role)).size}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* User Create/Edit Dialog */}
      <Dialog 
        open={userDialogOpen} 
        onClose={() => setUserDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab icon={<PersonIcon />} iconPosition="start" label="Basic Info" />
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Organization" />
            {!editingUser && <Tab icon={<LockIcon />} iconPosition="start" label="Security" />}
          </Tabs>

          {/* Basic Info Tab */}
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              <TextField
                label="Full Name"
                fullWidth
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
              <TextField
                label="Email Address"
                fullWidth
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                disabled={!!editingUser}
              />
              <TextField
                label="Phone Number"
                fullWidth
                value={userForm.phone}
                onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment>
                }}
              />
              <FormControl fullWidth required error={!!formErrors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  label="Role"
                >
                  {availableRoles.map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={userForm.status === 'Active'}
                    onChange={(e) => setUserForm({ 
                      ...userForm, 
                      status: e.target.checked ? 'Active' : 'Inactive' 
                    })}
                  />
                }
                label="Active User"
              />
            </Stack>
          </TabPanel>

          {/* Organization Tab */}
          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <TextField
                label="Department"
                fullWidth
                value={userForm.department}
                onChange={(e) => setUserForm({ ...userForm, department: e.target.value })}
              />
              <FormControl fullWidth required error={!!formErrors.companies}>
                <InputLabel>Assigned Companies</InputLabel>
                <Select
                  multiple
                  value={userForm.companies}
                  onChange={(e) => setUserForm({ 
                    ...userForm, 
                    companies: typeof e.target.value === 'string' 
                      ? e.target.value.split(',') 
                      : e.target.value 
                  })}
                  label="Assigned Companies"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {mockCompanies.map((company) => (
                    <MenuItem key={company} value={company}>
                      {company}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.companies && <FormHelperText>{formErrors.companies}</FormHelperText>}
              </FormControl>
            </Stack>
          </TabPanel>

          {/* Security Tab (only for new users) */}
          {!editingUser && (
            <TabPanel value={tabValue} index={2}>
              <Stack spacing={3}>
                <TextField
                  label="Password"
                  fullWidth
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  error={!!formErrors.password}
                  helperText={formErrors.password || 'Minimum 8 characters'}
                  required
                />
                <TextField
                  label="Confirm Password"
                  fullWidth
                  type="password"
                  value={userForm.confirmPassword}
                  onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  required
                />
                <Alert severity="info">
                  The user will receive an email with their login credentials and instructions to set up their account.
                </Alert>
              </Stack>
            </TabPanel>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
          >
            {editingUser ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user <strong>{deletingUser?.name}</strong>? 
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All data associated with this user will be permanently removed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography>
            Send a password reset email to <strong>{resetPasswordUser?.email}</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            The user will receive an email with instructions to reset their password.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmResetPassword} variant="contained">
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
